import type { OnboardingFieldOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Field, Input, Select, cn } from "@sagot/ui";
import { PillChoice } from "../auth/components/pill-choice";
import { fieldHelp, localized, type ProfileAnswers } from "./ranking";

interface ProfileFieldsProps {
  fields: readonly OnboardingFieldOut[];
  answers: ProfileAnswers;
  country: string;
  onChange: (answers: ProfileAnswers) => void;
  /** Keys of required questions left unanswered (after a submit attempt). */
  missing?: readonly string[];
}

/** Entrance for questions that appear when the template changes (existing ones are keyed and stay put). */
const ENTER = "animate-fade-in motion-reduce:animate-none";

/**
 * Template follow-up questions (`onboarding_fields`). Short controls sit two per row from the
 * `sm` breakpoint, multi-choice questions take the full row, and rows align to the top so a
 * question with a longer hint never stretches its neighbour.
 */
export function ProfileFields({ fields, answers, country, onChange, missing = [] }: ProfileFieldsProps) {
  const { t, i18n } = useTranslation();
  if (!fields.length) return null;
  const set = (key: string, value: string | string[]) => { onChange({ ...answers, [key]: value }); };
  return (
    <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2" data-testid="profile-fields">
      {fields.map((field) => {
        const label = localized(field.label, i18n.language);
        const hint = fieldHelp(field, country, i18n.language) || undefined;
        const error = missing.includes(field.key) ? t("onboarding.org.fieldRequired") : undefined;
        const value = answers[field.key];
        const options = (field.options ?? []).map((o) => ({ value: o.value, label: localized(o.label, i18n.language) }));
        if (field.type === "multiselect") {
          const labelId = `profile-${field.key}`;
          return (
            <div key={field.key} className={cn("min-w-0 sm:col-span-2", ENTER)}>
              <span className="label" id={labelId}>
                {label}
              </span>
              <PillChoice aria-labelledby={labelId} size="sm" multiple value={Array.isArray(value) ? value : []} onChange={(next) => { set(field.key, next); }} options={options} />
              {hint ? <p className="mt-1.5 text-xs text-fg-subtle">{hint}</p> : null}
              {error ? <p className="mt-1.5 text-xs font-semibold text-danger" role="alert">{error}</p> : null}
            </div>
          );
        }
        return (
          <Field key={field.key} className={cn("min-w-0", ENTER)} label={label} hint={hint} error={error}>
            {field.type === "select" ? (
              <Select value={typeof value === "string" ? value : ""} onChange={(event) => { set(field.key, event.target.value); }}>
                <option value="" disabled>
                  —
                </option>
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            ) : (
              <Input value={typeof value === "string" ? value : ""} maxLength={200} onChange={(event) => { set(field.key, event.target.value); }} />
            )}
          </Field>
        );
      })}
    </div>
  );
}
